import { useId, useMemo, useReducer, useRef, useState } from 'react';
import { CommandId, PromotionId, SELECTION_MARKET, type Balance, type Bet, type OddsLine, type RoundSnapshot, type GameSelection } from '@urbanrats/contracts';
import { money, promoPayout, type BetFunding } from '@urbanrats/domain';
import type { ClientGateways, ConnectionState } from '../gateway/index.ts';
import { NetworkError } from '../gateway/types.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { draftEntries, draftReducer, draftTotal, initialDraft } from '../lib/draft.ts';
import { fmtAtomic, fmtMoney, fmtOdds, fmtShort, localiseDecimal, parseAmountInput } from '../lib/format.ts';
import { conditionText, winningCounts } from '../lib/gameText.ts';
import { Approx } from './Approx.tsx';
import { rewardLabel, useRewards } from './RewardsPanel.tsx';

interface Props {
  gw: ClientGateways;
  round: RoundSnapshot;
  balance: Balance | null;
  connection: ConnectionState;
  /** Accepted (or later) bets in this round. */
  roundBets: Bet[];
  /** Lines of the most recent earlier round with accepted bets, for "Repeat last". */
  lastRoundLines: Partial<Record<GameSelection, string>> | null;
  /** Additional reason betting is blocked (e.g. video source unhealthy). */
  extraBlock?: string | null;
  /** LIVE: the board shows no numbers or odds (the round's window is over, or the next round's intro runs). */
  concealed?: boolean;
}

/** `pending`: the server answered but the casino wallet has not confirmed the stake yet (never shown as accepted). */
type LineResult = { selection: GameSelection; amount: bigint; ok: boolean; reason?: string; betId?: string; pending?: boolean };
/** States after which a stake that was waiting for the wallet is known not to have been taken. */
const NOT_TAKEN = new Set(['REJECTED', 'COMPENSATION_PENDING', 'COMPENSATED']);

const ACTIVE_STATES = new Set(['ACCEPTED', 'RESULT_WON', 'RESULT_LOST', 'CREDIT_PENDING', 'SETTLED', 'REFUND_PENDING', 'VOIDED']);

export function BetPanel({ gw, round, balance, connection, roundBets, lastRoundLines, extraBlock = null, concealed = false }: Props) {
  const { t, intl } = useI18n();
  const cur = gw.currency;
  const [draft, dispatch] = useReducer(draftReducer, gw.chipsAtomic[2] ?? gw.chipsAtomic[0] ?? '1', initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<LineResult[]>([]);
  // Stable command ids per selection until the server answers: a retry after a network error or a
  // double click re-sends the same command and is debited at most once.
  const commandIds = useRef(new Map<GameSelection, string>());

  const fmt = (v: bigint) => fmtAtomic(v, cur.scale, cur.code, intl);
  const label = (s: GameSelection) => t(`sel.${s}` as MessageKey);
  const cond = (s: GameSelection) => conditionText(t, round.manifest.rules, s);
  const { promo, items } = useRewards(gw);
  const [rewardId, setRewardId] = useState<string | null>(null);
  const [amountText, setAmountText] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  // The blank chip: a stake typed by the player. Its form opens when the chip is tapped.
  const [customOpen, setCustomOpen] = useState(false);
  const ids = { reward: useId(), amount: useId(), amountErr: useId() };
  const gameId = gw.gameInfo.gameId;
  const usable = items.filter((p) => p.state === 'GRANTED' && p.games.includes(gameId) && p.currency === cur.code);
  const reward = usable.find((p) => p.promotionId === rewardId) ?? null;
  const freebet = reward?.kind === 'FREEBET' && reward.faceValue ? reward : null;
  const lightningOf = (s: GameSelection) => round.manifest.lightning?.boosts.find((b) => b.selection === s) ?? null;
  const limits = { min: BigInt(gw.stakeLimits.minAtomic), max: BigInt(gw.stakeLimits.maxAtomic) };

  const open = round.state === 'BETTING_OPEN' && !extraBlock && !concealed;
  const online = connection === 'connected';
  const entries = draftEntries(draft.lines);
  const total = draftTotal(draft.lines);
  // A freebet debits no cash: the balance check applies to cash stakes only.
  const cashTotal = freebet ? 0n : total;
  const bal = balance ? BigInt(balance.balance.amountAtomic) : null;
  const insufficient = bal !== null && cashTotal > bal;
  const rewardProblem =
    reward && entries.length !== 1
      ? t('bet.rewardOneLine')
      : freebet && entries.length === 1 && lightningOf(entries[0]![0]) && !promo?.policy.freebetWithLightning
        ? t('rewards.stackFreebetLightning')
        : freebet && entries.length === 1 && entries[0]![1].toString() !== freebet.faceValue!.amountAtomic
          ? t('bet.rewardFreebetFace', { amount: fmtMoney(freebet.faceValue!, intl) })
          : null;
  const canConfirm = open && online && !submitting && total > 0n && !insufficient && !rewardProblem;

  const accepted = useMemo(() => {
    const m = new Map<GameSelection, bigint>();
    for (const b of roundBets) if (ACTIVE_STATES.has(b.state)) m.set(b.selection, (m.get(b.selection) ?? 0n) + BigInt(b.stake.amountAtomic));
    return m;
  }, [roundBets]);

  const odds = (s: GameSelection) => round.manifest.odds.find((o) => o.selection === s)!;
  // Same formula and cap the server applies at settlement (PromoMathPolicy); the server's quote is final.
  const preview = (s: GameSelection, amt: bigint) => {
    const funding: BetFunding = freebet && freebet.freebetPolicy ? { kind: 'FREEBET', faceValue: freebet.faceValue!, policy: freebet.freebetPolicy } : { kind: 'CASH' };
    return promoPayout(
      {
        stake: money(amt, cur.code, cur.scale),
        odds: odds(s).odds,
        lightning: lightningOf(s)?.factor ?? null,
        boostBps: reward?.kind === 'BOOST' ? (reward.boostBps ?? 0) : 0,
        cap: promo?.policy.payoutCap ?? null,
        funding,
      },
      true,
    );
  };

  const setAmount = () => {
    const r = parseAmountInput(amountText, intl, cur.scale);
    const example = localiseDecimal(cur.scale > 0 ? '1234.5' : '1234', intl);
    if (!r.ok) {
      setAmountError(t(`amount.${r.error.code}` as MessageKey, { example, currency: cur.code, scale: cur.scale }));
      return;
    }
    if (r.value === 0n) return setAmountError(t('amount.ZERO'));
    if (r.value < limits.min || r.value > limits.max) return setAmountError(t('amount.LIMIT', { min: fmt(limits.min), max: fmt(limits.max) }));
    setAmountError(null);
    dispatch({ type: 'chip', atomic: r.value.toString() });
    setCustomOpen(false);
  };

  async function confirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setResults([]);
    const out = await Promise.all(
      entries.map(async ([selection, amount]): Promise<LineResult> => {
        let id = commandIds.current.get(selection);
        if (!id) {
          id = crypto.randomUUID();
          commandIds.current.set(selection, id);
        }
        try {
          const res = await gw.game.placeBet({
            commandId: CommandId.parse(id),
            gameRoundId: round.gameRoundId,
            selection,
            stake: money(amount, cur.code, cur.scale),
            expectedOddsVersion: round.manifest.oddsVersion,
            rewardId: reward ? PromotionId.parse(reward.promotionId) : null,
          });
          commandIds.current.delete(selection);
          if (!res.ok) return { selection, amount, ok: false, reason: res.error.code };
          // Accepted only when the server says so; a stake still waiting for the casino wallet is not accepted.
          if (res.value.state === 'ACCEPTED') return { selection, amount, ok: true, betId: res.value.betId };
          if (res.value.state === 'DEBIT_PENDING') return { selection, amount, ok: false, pending: true, betId: res.value.betId };
          return { selection, amount, ok: false, reason: res.value.rejectReason ?? 'WALLET_UNAVAILABLE' };
        } catch (e) {
          // Not delivered: keep the command id so a retry cannot double-debit.
          return { selection, amount, ok: false, reason: e instanceof NetworkError ? 'NETWORK' : 'NETWORK' };
        }
      }),
    );
    for (const r of out) if (r.ok) dispatch({ type: 'accepted', selection: r.selection });
    if (out.some((r) => r.ok)) setRewardId(null);
    setResults(out);
    setSubmitting(false);
  }

  const option = (o: OddsLine) => {
    const hide = concealed;
    const s = o.selection;
    const d = BigInt(draft.lines[s] ?? '0');
    const a = accepted.get(s) ?? 0n;
    const lt = lightningOf(s);
    const ltText = lt ? t('bet.lightning', { tier: t(`tier.${lt.tier}` as MessageKey), factor: fmtOdds(lt.factor, intl).replace('×', '') }) : null;
    const counts = winningCounts(round.manifest.rules, s);
    return (
      <button
        key={s}
        type="button"
        className={`option ${d > 0n ? 'has-draft' : ''} ${a > 0n && !hide ? 'has-bet' : ''} ${lt && !hide ? 'lightning' : ''} ${hide ? 'concealed' : ''}`}
        disabled={!open || !online || submitting}
        aria-pressed={d > 0n}
        // Every tap adds the chosen chip to the selection (Undo / Clear take it off).
        onClick={() => (freebet ? dispatch({ type: 'set', lines: { [s]: freebet.faceValue!.amountAtomic } }) : dispatch({ type: 'add', selection: s }))}
        aria-label={`${t('bet.addChip', { amount: fmt(freebet ? BigInt(freebet.faceValue!.amountAtomic) : BigInt(draft.chipAtomic)), selection: label(s) })}. ${cond(s)}. ${fmtOdds(o.odds, intl)}${lt ? `. ${t('bet.lightningAria', { tier: t(`tier.${lt.tier}` as MessageKey), factor: fmtOdds(lt.factor, intl).replace('×', '') })}` : ''}`}
        data-testid={`option-${s}`}
      >
        <span className="option-name">{bands ? t(`board.${s}` as MessageKey) : label(s)}</span>
        <span className="option-main">{hide ? '–' : bands ? boardCounts(s) : (counts ?? cond(s))}</span>
        <span className="option-odds">{hide ? '×–' : fmtOdds(o.odds, intl)}</span>
        {ltText && !hide && (
          <span className={`lightning-tag tier-${lt!.tier}`} data-testid={`lightning-${s}`}>
            {ltText}
          </span>
        )}
        {counts && <span className="sr-only">{cond(s)}</span>}
        {!hide && (d > 0n || a > 0n) && (
          <span className="option-stakes">
            {d > 0n && (
              <span className="bet-chip bet-chip-draft" title={t('bet.draftOn', { amount: fmt(d) })}>
                <span className="sr-only">{t('bet.draftOn', { amount: fmt(d) })}</span>
                <span aria-hidden>{fmtShort(d, cur.scale, intl)}</span>
              </span>
            )}
            {a > 0n && (
              <span className="bet-chip bet-chip-accepted" title={t('bet.acceptedOn', { amount: fmt(a) })}>
                <span className="sr-only">✓ {t('bet.acceptedOn', { amount: fmt(a) })}</span>
                <span aria-hidden>✓ {fmtShort(a, cur.scale, intl)}</span>
              </span>
            )}
          </span>
        )}
      </button>
    );
  };

  // Count bands: the bets laid out like the counting bar — few · exactly L · middle · exactly H · many,
  // then "exactly one of the two limits" — under one plain question.
  const rules = round.manifest.rules;
  const bands = rules.kind === 'COUNT_BANDS' ? rules.thresholds : null;
  const boardCounts = (sel: GameSelection) =>
    !bands ? '' : sel === 'UNDER' ? `≤ ${bands.low - 1}` : sel === 'OVER' ? `≥ ${bands.high + 1}` : sel === 'EXACT' ? t('board.either', { low: bands.low, high: bands.high }) : (winningCounts(rules, sel) ?? '');
  const byId = (sel: GameSelection) => round.manifest.odds.find((o) => o.selection === sel);
  const pick = (sels: GameSelection[]) => sels.map(byId).filter((o): o is OddsLine => !!o);
  // Two rows of three: the ranges (few · middle · many), then the exact hits (L · H · either limit).
  const ranges = bands ? pick(['UNDER', 'RANGE', 'OVER']) : [];
  const exacts = bands ? pick(['EXACT_LOW', 'EXACT_HIGH', 'EXACT']) : [];
  const main = round.manifest.odds.filter((o) => SELECTION_MARKET[o.selection] === 'MAIN');
  const side = round.manifest.odds.filter((o) => SELECTION_MARKET[o.selection] === 'EXACT_SIDE');

  let blockedMsg: string | null = null;
  if (extraBlock) blockedMsg = extraBlock;
  else if (!online) blockedMsg = t('bet.offline');
  else if (!open) blockedMsg = `${t('bet.closed')}. ${t('bet.closedHint')}`;

  return (
    <section className="bet-panel" aria-label={t('bet.title')} data-testid="bet-panel">
      <div className="bet-head">
        <h2>{t('bet.title')}</h2>
        {gw.session.moneyMode !== 'REAL' && <p className="note">{t('bet.exampleOdds')}</p>}
        <p className="note note-mobile">{t('bet.noDebitYet')}</p>
      </div>

      <fieldset className="chips" disabled={!open || !online || submitting || !!freebet}>
        <legend>{t('bet.chip')}</legend>
        {gw.chipsAtomic.map((c, i) => (
          <label key={c} className={`chip-opt chip-c${i % 6} ${draft.chipAtomic === c ? 'on' : ''}`}>
            <input type="radio" name="chip" value={c} checked={draft.chipAtomic === c} onChange={() => dispatch({ type: 'chip', atomic: c })} aria-label={t('bet.chipLabel', { amount: fmt(BigInt(c)) })} />
            <span aria-hidden>{fmtShort(BigInt(c), cur.scale, intl)}</span>
          </label>
        ))}
        {/* The blank chip: the player writes its value. */}
        <button
          type="button"
          className={`chip-opt chip-blank ${!gw.chipsAtomic.includes(draft.chipAtomic) ? 'on' : ''}`}
          aria-expanded={customOpen}
          aria-label={t('bet.amount')}
          onClick={() => setCustomOpen((v) => !v)}
          data-testid="chip-custom"
        >
          <span aria-hidden>{!gw.chipsAtomic.includes(draft.chipAtomic) ? fmtShort(BigInt(draft.chipAtomic), cur.scale, intl) : '✎'}</span>
        </button>
      </fieldset>
      {customOpen && (
        <form
          className="amount-row"
          onSubmit={(e) => {
            e.preventDefault();
            setAmount();
          }}
        >
          <label htmlFor={ids.amount}>{t('bet.amount')}</label>
          <input
            id={ids.amount}
            inputMode="decimal"
            autoComplete="off"
            autoFocus
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            aria-invalid={amountError ? true : undefined}
            aria-describedby={amountError ? ids.amountErr : undefined}
            disabled={!open || submitting || !!freebet}
            placeholder={fmtShort(BigInt(draft.chipAtomic), cur.scale, intl)}
            data-testid="amount-input"
          />
          <button type="submit" className="ghost-btn bordered" disabled={!open || submitting || !!freebet || amountText.trim() === ''}>
            {t('bet.amountSet')}
          </button>
          {amountError && (
            <p id={ids.amountErr} className="field-error" role="alert" data-testid="amount-error">
              {amountError}
            </p>
          )}
        </form>
      )}

      {promo && (promo.flags.freebets || promo.flags.boosts) && usable.length > 0 && (
        <div className="reward-row">
          <label htmlFor={ids.reward}>{t('bet.reward')}</label>
          <select
            id={ids.reward}
            value={rewardId ?? ''}
            disabled={!open || submitting}
            data-testid="reward-select"
            onChange={(e) => {
              const id = e.target.value || null;
              setRewardId(id);
              const p = usable.find((x) => x.promotionId === id);
              // A freebet has a fixed stake: the draft becomes that one line when a selection is tapped.
              if (p?.kind === 'FREEBET') dispatch({ type: 'clear' });
            }}
          >
            <option value="">{t('bet.rewardNone')}</option>
            {usable.map((p) => (
              <option key={p.promotionId} value={p.promotionId}>
                {p.kind === 'FREEBET' && p.faceValue
                  ? t('bet.rewardFreebet', { amount: fmtMoney(p.faceValue, intl), policy: t(`rewards.policy.${p.freebetPolicy}` as MessageKey) })
                  : rewardLabel(p, t, intl)}
              </option>
            ))}
          </select>
          {freebet && <p className="note">{t('bet.rewardFreebetHint', { amount: fmtMoney(freebet.faceValue!, intl) })}</p>}
        </div>
      )}

      {blockedMsg && (
        <p className="blocked" role="status" data-testid="bet-blocked">
          {blockedMsg}
        </p>
      )}

      {round.manifest.lightning && round.manifest.lightning.boosts.length > 0 && <p className="note lightning-note">⚡ {t('bet.lightningNote')}</p>}
      {bands ? (
        <>
          <p className="board-question">{t('board.question', { sec: Math.round((Date.parse(round.manifest.timing.captureEndAt) - Date.parse(round.manifest.timing.captureStartAt)) / 1000) })}</p>
          <div className="band-board" data-testid="band-board">
            {ranges.map(option)}
          </div>
          {exacts.length > 0 && <div className="band-board band-board-exact">{exacts.map(option)}</div>}
        </>
      ) : (
        <>
      <h3 className="market-title">{t('bet.main')}</h3>
      <div className="options">{main.map(option)}</div>
      {side.length > 0 && (
        <>
          <h3 className="market-title">{t('bet.side')}</h3>
          <p className="note">{t('bet.sideNote')}</p>
          <div className="options">{side.map(option)}</div>
        </>
      )}
        </>
      )}

      {entries.length > 0 && (
        <ul className="draft-lines" aria-label={t('bet.total')}>
          {entries.map(([s, amt]) => {
            const p = preview(s, amt);
            return (
              <li key={s}>
                {t('bet.potential', { selection: label(s), gross: fmt(BigInt(p.credit.amountAtomic)), net: fmt(BigInt(p.net.amountAtomic)) })}
              </li>
            );
          })}
        </ul>
      )}

      <div className="draft-tools">
        <button type="button" className="tool-btn" disabled={draft.past.length === 0 || submitting} onClick={() => dispatch({ type: 'undo' })} data-testid="tool-undo">
          <span aria-hidden>↶</span> {t('bet.undo')}
        </button>
        <button type="button" className="tool-btn" disabled={entries.length === 0 || submitting} onClick={() => dispatch({ type: 'clear' })} data-testid="tool-clear">
          <span aria-hidden>✕</span> {t('bet.clear')}
        </button>
        {/* ×2: doubles the draft; with an empty draft it puts this round's accepted bets on again. */}
        <button
          type="button"
          className="tool-btn tool-strong"
          disabled={!open || !online || submitting || !!freebet || (entries.length === 0 && accepted.size === 0)}
          title={t('bet.doubleHint')}
          onClick={() =>
            entries.length > 0
              ? dispatch({ type: 'double', maxAtomic: limits.max.toString() })
              : dispatch({ type: 'repeat', lines: Object.fromEntries([...accepted].map(([k, v]) => [k, (v > limits.max ? limits.max : v).toString()])) })
          }
          data-testid="tool-double"
        >
          ×2
        </button>
        <button type="button" className="tool-btn tool-strong" title={t('bet.repeatHint')} disabled={!open || !online || submitting || !!freebet || !lastRoundLines} onClick={() => lastRoundLines && dispatch({ type: 'repeat', lines: lastRoundLines })} data-testid="tool-repeat">
          <span aria-hidden>⟳</span> {t('bet.repeat')}
        </button>
      </div>

      {results.length > 0 && (
        <ul className="line-results" role="status" data-testid="bet-results">
          {results.map((r) => {
            const v = { selection: label(r.selection), amount: fmt(r.amount) };
            // A stake that was waiting for the casino wallet follows the bet's real state.
            const live = r.pending && r.betId ? roundBets.find((b) => b.betId === r.betId) : undefined;
            const state = r.ok ? 'ok' : !r.pending ? 'bad' : live && ACTIVE_STATES.has(live.state) ? 'ok' : live && NOT_TAKEN.has(live.state) ? 'bad' : 'pending';
            return (
              <li key={r.selection} className={state} data-state={state}>
                {state === 'ok'
                  ? `✓ ${t('bet.result.accepted', v)}`
                  : state === 'pending'
                    ? `⏳ ${t('bet.result.pending', v)}`
                    : r.pending
                      ? `✕ ${t('bet.result.notConfirmed', v)}`
                      : `✕ ${t('bet.result.rejected', { ...v, reason: t(`reason.${r.reason}` as MessageKey) })}`}
              </li>
            );
          })}
        </ul>
      )}

      <div className="confirm-bar" data-testid="confirm-bar">
        <div className="confirm-summary">
          <span className="muted">{t('bet.total')}</span>
          <strong className="tabular" data-testid="draft-total" data-atomic={total.toString()}>
            {fmt(total)}
          </strong>
          {total > 0n && <Approx money={money(total, cur.code, cur.scale)} />}
        </div>
        {promo && <p className="note small">{t('bet.cap', { cap: fmtMoney(promo.policy.payoutCap, intl) })}</p>}
        {insufficient && (
          <p className="field-error" role="alert">
            {t('bet.insufficient')}
          </p>
        )}
        {rewardProblem && entries.length > 0 && (
          <p className="field-error" role="alert" data-testid="reward-problem">
            {rewardProblem}
          </p>
        )}
        <button type="button" className="confirm" disabled={!canConfirm} onClick={() => void confirm()} data-testid="confirm">
          {submitting ? t('bet.pending') : !open ? t('bet.closed') : total === 0n ? t('bet.confirmEmpty') : t('bet.confirm', { amount: fmt(total) })}
        </button>
        <p className="note small">{t('bet.noDebitYet')}</p>
      </div>
    </section>
  );
}
